import {
  ArrowDownIcon,
  ChevronLeftIcon,
  XIcon,
} from '@heroicons/react/outline';
import { Drawer } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import StarWithoutBg from 'assets/Icons/StarWithoutBg';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import { triggerSessionEvent } from '@/components/Layout/ProductsHeader';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';

import { isBannerVisible } from '@/atoms/isBannerVisible';
import { user } from '@/atoms/user';
import { askQuestion, getPrompts, getSessionMessages } from '@/services/AiChat';
import { User } from '@/utils/interfaces/User';

import ChatInput from './ChatInput/ChatInput';
import ChatMessages, { ChatMessage } from './ChatMessages';
import SidebarChats, { ChatItem } from './SidebarChats';

interface ZAIChatDrawerProps {
  open: boolean;
  onClose: () => void;
  bookId?: string;
  initialSessionId?: string;
}

const makeMessageId = (
  sessionId: string,
  sourceId: string,
  role: 'user' | 'assistant',
) => `${sessionId}-${sourceId}-${role}`;

const ZAIChatDrawer: React.FC<ZAIChatDrawerProps> = ({
  open,
  onClose,
  bookId,
  initialSessionId,
}) => {
  const [chats, setChats] = useState<ChatItem[]>([]);
  const [activeChatId, setActiveChatId] = useState('new');
  const [messagesMap, setMessagesMap] = useState<Record<string, ChatMessage[]>>(
    {},
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [prompts, setPrompts] = useState<string[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [messagesPageMap, setMessagesPageMap] = useState<
    Record<string, number>
  >({});
  const [messagesHasMoreMap, setMessagesHasMoreMap] = useState<
    Record<string, boolean>
  >({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [prompt, setPrompt] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const isBannerVisibleValue = useRecoilValue<boolean | null>(isBannerVisible);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastUpdateSourceRef = useRef<'user' | 'assistant' | null>(null);
  const prevScrollHeightRef = useRef(0);

  const screens = useBreakpoint();
  const ZAI_SESSION_KEY = 'zai_screen_session';

  const userData: User | undefined = useRecoilValue(user);
  const messages = messagesMap[activeChatId] || [];
  const router = useRouter();

  const isTempChat = (id?: string) => id?.startsWith('new-');

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      const hasOverflow = container.scrollHeight > container.clientHeight + 8;

      const nearBottom = distanceFromBottom < 80;

      setShowScrollDown(hasOverflow && !nearBottom);
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const heightDiff = container.scrollHeight - prevScrollHeightRef.current;

    if (lastUpdateSourceRef.current === 'user' && heightDiff < 600) {
      bottomRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }

    prevScrollHeightRef.current = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  const handleNewChat = () => {
    const existingTemp = chats.find((c) => isTempChat(c.id));
    if (existingTemp) {
      setActiveChatId(existingTemp.id);
      return;
    }

    const id = `new-${crypto.randomUUID()}`;

    setChats((prev) => [
      { id, title: 'New Chat', updatedAt: 'Today' },
      ...prev,
    ]);

    setMessagesMap((prev) => ({
      ...prev,
      [id]: [],
    }));

    setActiveChatId(id);
  };

  const fetchSessionMessages = async (
    sessionId: string,
    page = 1,
    append = false,
  ) => {
    if (sessionId.startsWith('new')) return;
    if (loadingHistory) return;

    try {
      setLoadingHistory(true);

      const res = await getSessionMessages(sessionId, page, 20);
      const list = res?.data ?? [];
      const hasMore = res?.pagination?.has_next ?? false;

      const mapped: ChatMessage[] = list.reverse().flatMap((m: any) => {
        const messages: ChatMessage[] = [];

        if (m.query) {
          messages.push({
            id: makeMessageId(sessionId, m.id, 'user'),
            role: 'user',
            content: m.query,
            feedback: m.feedback ?? 0,
            sourceDocuments: m.source_documents,
          });
        }

        if (m.result) {
          messages.push({
            id: makeMessageId(sessionId, m.id, 'assistant'),
            role: 'assistant',
            content: m.result,
            feedback: m.feedback ?? 0,
            sourceDocuments: m.source_documents,
          });
        }

        return messages;
      });

      setMessagesMap((prev) => ({
        ...prev,
        [sessionId]: append ? [...mapped, ...(prev[sessionId] ?? [])] : mapped,
      }));

      setMessagesPageMap((prev) => ({
        ...prev,
        [sessionId]: page,
      }));

      setMessagesHasMoreMap((prev) => ({
        ...prev,
        [sessionId]: hasMore,
      }));
    } catch (err: any) {
      notify(formateErrorObject(err), 'error');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchPrompts = async () => {
    try {
      setPromptsLoading(true);

      const data = await getPrompts();
      const list = Array.isArray(data) ? data.map((p: any) => p.text ?? p) : [];

      setPrompts(list);
    } catch (error) {
      setPrompts([]);
    } finally {
      setPromptsLoading(false);
    }
  };

  const logAiQuery = (query: string) => {
    const eventParams: EventDetails = {
      event_name: 'ai_query',
      source: 'ask_ai',
      source_type: 'global',
      source_id: null,
      sub_source: null,
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
      meta: { click_source: 'ask_ai_global_screen', ai_query: query },
    };

    logEvent({
      eventDetails: eventParams,
      userData,
      pathname: router.pathname,
    });
  };

  useEffect(() => {
    const handleUnload = () => {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem(ZAI_SESSION_KEY);
      if (!raw) return;

      try {
        const { start } = JSON.parse(raw);
        if (!start) return;

        triggerSessionEvent(
          new Date(start),
          new Date(),
          userData as User,
          router.pathname,
        );
      } catch {
        // ignore
      } finally {
        localStorage.removeItem(ZAI_SESSION_KEY);
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    fetchPrompts();
  }, [open]);

  const safeArray = <T,>(value: T[] | undefined) =>
    Array.isArray(value) ? value : [];

  const handleSend = async () => {
    if (!prompt.trim() || processing) return;

    let currentChatId = activeChatId;

    if (currentChatId === 'new') {
      const existingTemp = chats.find((c) => isTempChat(c.id));

      if (existingTemp) {
        currentChatId = existingTemp.id;
        setActiveChatId(existingTemp.id);
      } else {
        const id = `new-${crypto.randomUUID()}`;

        setChats((prev) => [
          { id, title: 'New Chat', updatedAt: 'Today' },
          ...prev,
        ]);

        setMessagesMap((prev) => ({
          ...prev,
          [id]: [],
        }));

        currentChatId = id;
        setActiveChatId(id);
      }
    }

    const tempId = crypto.randomUUID();

    const userMsg: ChatMessage = {
      id: makeMessageId(currentChatId, tempId, 'user'),
      role: 'user',
      content: prompt,
      feedback: 0,
      sourceDocuments: [],
    };

    lastUpdateSourceRef.current = 'user';

    setMessagesMap((prev) => ({
      ...prev,
      [currentChatId]: [...safeArray(prev[currentChatId]), userMsg],
    }));

    logAiQuery(userMsg.content);
    setPrompt('');
    setProcessing(true);

    try {
      const data = await askQuestion({
        question: userMsg.content,
        ...(currentChatId &&
        !currentChatId.startsWith('new') &&
        currentChatId !== '1'
          ? { session_id: currentChatId }
          : {}),
        ...(bookId ? { book_id: bookId } : {}),
      });

      const resolvedSessionId = data.session_id || currentChatId;
      const wasTemp = currentChatId.startsWith('new-');

      if (wasTemp && resolvedSessionId) {
        setChats((prev) =>
          prev.map((c) =>
            c.id === currentChatId
              ? {
                  id: resolvedSessionId,
                  title: userMsg.content.slice(0, 40),
                  updatedAt: 'Today',
                }
              : c,
          ),
        );

        setMessagesMap((prev) => ({
          ...prev,
          [resolvedSessionId]: safeArray(prev[currentChatId]),
        }));

        setActiveChatId(resolvedSessionId);
      }

      const aiMsg: ChatMessage = {
        id: makeMessageId(resolvedSessionId, data.id, 'assistant'),
        role: 'assistant',
        content: data.result,
        feedback: data.feedback ?? 0,
        sourceDocuments: [],
      };

      lastUpdateSourceRef.current = 'assistant';

      setMessagesMap((prev) => ({
        ...prev,
        [resolvedSessionId]: [...safeArray(prev[resolvedSessionId]), aiMsg],
      }));
    } catch (err: any) {
      notify(formateErrorObject(err), 'error');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (!open || !initialSessionId) return;

    const msgs = messagesMap[initialSessionId];

    if (Array.isArray(msgs) && msgs.length > 0) {
      setActiveChatId(initialSessionId);

      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      });
    }
  }, [open, initialSessionId, messagesMap]);

  const handleMessageScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;

    if (
      el.scrollTop < 80 &&
      messagesHasMoreMap[activeChatId] &&
      !loadingHistory
    ) {
      const nextPage = (messagesPageMap[activeChatId] ?? 1) + 1;
      fetchSessionMessages(activeChatId, nextPage, true);
    }
  };

  const handleSelectChat = (id: string, closeSidebar = false) => {
    setActiveChatId(id);

    if (!messagesMap[id] && !id.startsWith('new')) {
      fetchSessionMessages(id, 1);
    }

    if (closeSidebar) {
      setSidebarCollapsed(true);
    }

    if (!screens.md) setMobileSidebarOpen(false);
    bottomRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  };
  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        placement='right'
        width='100%'
        closable={false}
        mask={false}
        destroyOnHidden
        className='zai-drawer z-0'
      >
        <div
          className={clsx(
            isBannerVisibleValue
              ? 'h-[85.4%] 2xl:h-[87.3%] translate-y-[17%] 2xl:translate-y-[14.6%]'
              : 'h-[92.5%] translate-y-[8%]',
            'flex w-full bg-[#121212] text-white',
          )}
        >
          <div className='hidden md:block'>
            <SidebarChats
              collapsed={sidebarCollapsed}
              activeChatId={activeChatId}
              onSelectChat={(id) => handleSelectChat(id)}
              onNewChat={handleNewChat}
              setSidebarCollapsed={setSidebarCollapsed}
              chats={chats}
              setChats={setChats}
            />
          </div>

          <div className='flex flex-1 flex-col relative'>
            <div className='relative h-14 flex items-center px-4'>
              <div
                style={{
                  color:
                    'linear-gradient(270deg, #7E0D9D 0.3%, #7328F2 50%, #51A0F7 99.7%)',
                }}
                className='absolute left-1/2 -translate-x-1/2 flex items-center gap-2'
              >
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 17 17'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M6.2427 3.1862C7.02902 1.06122 7.42217 -0.00127041 8.11841 -0.00127041C8.81464 -0.00127041 9.2078 1.06122 9.99411 3.1862L10.5002 4.5538C10.6572 4.97815 10.7357 5.19033 10.8905 5.34511C11.0453 5.49989 11.2574 5.5784 11.6818 5.73543L13.0494 6.24148C15.1744 7.0278 16.2369 7.42095 16.2369 8.11719C16.2369 8.81342 15.1744 9.20658 13.0494 9.99289L11.6818 10.4989C11.2574 10.656 11.0453 10.7345 10.8905 10.8893C10.7357 11.044 10.6572 11.2562 10.5002 11.6806L9.99411 13.0482C9.2078 15.1732 8.81464 16.2356 8.11841 16.2356C7.42217 16.2356 7.02902 15.1732 6.2427 13.0482L5.73665 11.6806C5.57962 11.2562 5.50111 11.044 5.34633 10.8893C5.19155 10.7345 4.97937 10.656 4.55502 10.4989L3.18742 9.99289C1.06244 9.20658 -4.97103e-05 8.81342 -4.97103e-05 8.11719C-4.97103e-05 7.42095 1.06244 7.0278 3.18742 6.24148L4.55502 5.73543C4.97937 5.5784 5.19155 5.49989 5.34633 5.34511C5.50111 5.19033 5.57962 4.97815 5.73665 4.5538L6.2427 3.1862Z'
                    fill='url(#paint0_linear_18134_48087)'
                  />
                  <defs>
                    <linearGradient
                      id='paint0_linear_18134_48087'
                      x1='18.0582'
                      y1='8.11719'
                      x2='-1.82135'
                      y2='8.11718'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#7E0D9D' />
                      <stop offset='0.5' stopColor='#7328F2' />
                      <stop offset='1' stopColor='#51A0F7' />
                    </linearGradient>
                  </defs>
                </svg>

                <span className='font-bold'>zAI</span>
              </div>
              <ShouldRender check={!screens.md}>
                <button
                  onClick={onClose}
                  className='rounded-full w-min text-white'
                >
                  <ChevronLeftIcon className='h-5 w-5 ' />
                </button>
              </ShouldRender>
              {screens.md ? (
                <button
                  onClick={onClose}
                  className='ml-auto text-grayZl-500 border border-grayZl-700 rounded-full p-1 hover:text-white'
                >
                  <XIcon className='h-4 w-4' />
                </button>
              ) : (
                <button
                  onClick={() => !screens.md && setMobileSidebarOpen(true)}
                  className='flex items-center ml-auto'
                >
                  <svg
                    width='24'
                    height='24'
                    viewBox='0 0 24 24'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M12 21C9.7 21 7.69583 20.2375 5.9875 18.7125C4.27917 17.1875 3.3 15.2833 3.05 13H5.1C5.33333 14.7333 6.10417 16.1667 7.4125 17.3C8.72083 18.4333 10.25 19 12 19C13.95 19 15.6042 18.3208 16.9625 16.9625C18.3208 15.6042 19 13.95 19 12C19 10.05 18.3208 8.39583 16.9625 7.0375C15.6042 5.67917 13.95 5 12 5C10.85 5 9.775 5.26667 8.775 5.8C7.775 6.33333 6.93333 7.06667 6.25 8H9V10H3V4H5V6.35C5.85 5.28333 6.8875 4.45833 8.1125 3.875C9.3375 3.29167 10.6333 3 12 3C13.25 3 14.4208 3.2375 15.5125 3.7125C16.6042 4.1875 17.5542 4.82917 18.3625 5.6375C19.1708 6.44583 19.8125 7.39583 20.2875 8.4875C20.7625 9.57917 21 10.75 21 12C21 13.25 20.7625 14.4208 20.2875 15.5125C19.8125 16.6042 19.1708 17.5542 18.3625 18.3625C17.5542 19.1708 16.6042 19.8125 15.5125 20.2875C14.4208 20.7625 13.25 21 12 21ZM14.8 16.2L11 12.4V7H13V11.6L16.2 14.8L14.8 16.2Z'
                      fill='white'
                    />
                  </svg>
                </button>
              )}
            </div>

            <div className='flex-1 relative'>
              {messages.length === 0 ? (
                <div className='absolute inset-0 flex items-center justify-center'>
                  <div className='flex flex-col items-center md:h-[60%] gap-6 w-full'>
                    <div className='flex flex-col items-center justify-center'>
                      <div className='flex justify-center items-center w-[60px] h-[60px] md:w-[80px] md:h-[80px] rounded-full bg-[linear-gradient(270deg,#7E0D9D_0.3%,#7328F2_50%,#51A0F7_99.7%)]'>
                        <motion.div
                          initial={{ scale: 1, rotate: 0 }}
                          animate={{
                            rotate: [0, 45, 45, 180, 176, 180],
                            scale: [1, 0.6, 0.6, 1.15, 0.95, 1],
                          }}
                          transition={{
                            duration: 2.0,
                            times: [0, 0.35, 0.55, 0.8, 0.9, 1],
                            ease: 'easeInOut',
                            repeat: Infinity,
                          }}
                        >
                          <StarWithoutBg width={screens.md ? '32' : '24'} />
                        </motion.div>
                      </div>
                      <div className='text-lg font-semibold mt-6 w-5/6 md:w-full text-center whitespace-nowrap'>
                        Hello{userData?.name && `, `}{' '}
                        {userData?.name && userData?.name + '!'}
                      </div>
                    </div>

                    <div className='flex flex-wrap gap-2 justify-center max-w-xl'>
                      {promptsLoading &&
                        Array.from({ length: 3 }).map((_, i) => (
                          <div
                            key={i}
                            className='h-7 w-44 rounded-full bg-[#1E1E1F] animate-pulse'
                          />
                        ))}

                      {!promptsLoading &&
                        prompts.map((text) => (
                          <button
                            key={text}
                            onClick={() => setPrompt(text)}
                            className='px-3 py-1.5 text-[10px] md:text-xs rounded-lg  border border-[#282829] hover:bg-[#2A2A2B] transition'
                          >
                            {text}
                          </button>
                        ))}
                    </div>
                    <motion.div
                      initial={false}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -10, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                      className='w-full max-w-xl lg:max-w-2xl  pt-4'
                    >
                      <ChatInput
                        value={prompt}
                        onChange={setPrompt}
                        onSend={handleSend}
                        disabled={processing}
                      />
                    </motion.div>
                  </div>
                </div>
              ) : (
                <div
                  ref={scrollContainerRef}
                  onScroll={handleMessageScroll}
                  className='absolute inset-0 overflow-auto overscroll-contain zai-scrollbar'
                >
                  <ChatMessages
                    messages={messages}
                    loading={processing || loadingHistory}
                  />
                  <div ref={bottomRef} />
                </div>
              )}
              {showScrollDown && (
                <button
                  onClick={() =>
                    bottomRef.current?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'end',
                    })
                  }
                  className='absolute bottom-5 left-1/2 -translate-x-1/2 z-50 h-9 w-9 rounded-full bg-[#1E1E1F]/70 border border-[#2A2A2B]/60 text-white shadow-lg hover:bg-[#2A2A2B] transition'
                >
                  <ArrowDownIcon className='h-3.5 w-3.5 mx-auto' />
                </button>
              )}
            </div>

            {messages.length > 0 && (
              <motion.div
                initial={{ y: -16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
                className='pb-1'
              >
                <ChatInput
                  value={prompt}
                  onChange={setPrompt}
                  onSend={handleSend}
                  disabled={processing}
                  showDisclaimer={true}
                />
              </motion.div>
            )}
          </div>
        </div>
      </Drawer>
      <ShouldRender check={!screens.md}>
        <Drawer
          open={mobileSidebarOpen}
          placement='left'
          width='100%'
          closable={false}
          className='zai-drawer'
          onClose={() => setMobileSidebarOpen(false)}
        >
          <div
            className={clsx(
              isBannerVisibleValue
                ? 'h-[85.4%] 2xl:h-[87.3%] translate-y-[17%] 2xl:translate-y-[14.6%]'
                : 'h-[92.5%] translate-y-[8%]',
              'flex w-full bg-[#121212] text-white',
            )}
          >
            <SidebarChats
              collapsed={false}
              activeChatId={activeChatId}
              onSelectChat={(id) => handleSelectChat(id)}
              onNewChat={() => {
                handleNewChat();
                setMobileSidebarOpen(false);
              }}
              setSidebarCollapsed={setMobileSidebarOpen}
              chats={chats}
              setChats={setChats}
            />
          </div>
        </Drawer>
      </ShouldRender>
    </>
  );
};

export default ZAIChatDrawer;
